<template>
  <footer class="bg-[#060918] relative overflow-hidden">
    <!-- Top glow line -->
    <div class="hr-glow"></div>

    <!-- Main footer content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 sm:pt-20 pb-8">

      <!-- Top: Brand row -->
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-12 sm:mb-16">
        <div>
          <a href="/" class="text-2xl font-bold text-white tracking-tight inline-block mb-3">
            Team<span class="text-neon">Collab</span>
          </a>
          <p class="text-sm text-gray-500 leading-relaxed max-w-md">
            Produtos SaaS prontos para simplificar a gestão e impulsionar os resultados do seu negócio.
          </p>
        </div>
        <!-- Trust badges -->
        <div class="flex items-center gap-3 flex-shrink-0">
          <div class="flex items-center gap-1.5 bg-white/5 border border-white/5 rounded-lg px-3 py-1.5">
            <svg class="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"
              stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            <span class="text-[10px] text-gray-400 font-medium">LGPD</span>
          </div>
          <div class="flex items-center gap-1.5 bg-white/5 border border-white/5 rounded-lg px-3 py-1.5">
            <svg class="w-3.5 h-3.5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span class="text-[10px] text-gray-400 font-medium">SSL</span>
          </div>
          <div class="flex items-center gap-1.5 bg-white/5 border border-white/5 rounded-lg px-3 py-1.5">
            <svg class="w-3.5 h-3.5 text-neon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
            </svg>
            <span class="text-[10px] text-gray-400 font-medium">Cloud</span>
          </div>
        </div>
      </div>

      <!-- Separator -->
      <div class="hr-glow mb-12 sm:mb-16"></div>

      <!-- Columns: 2x2 mobile, 4 cols desktop -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-16 mb-14">

        <!-- Col 1: Links -->
        <div>
          <h4 class="text-xs text-gray-400 font-semibold uppercase tracking-[0.2em] mb-5">Links</h4>
          <ul class="space-y-3">
            <li><a @click.prevent="goToSection('#hero')" href="#hero"
                class="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer">Início</a></li>
            <li><a @click.prevent="goToSection('#servicos')" href="#servicos"
                class="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer">Serviços</a></li>
            <li><a @click.prevent="goToSection('#sobre')" href="#sobre"
                class="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer">Sobre</a></li>
            <li><a @click.prevent="goToSection('#contact')" href="#contact"
                class="text-sm text-gray-500 hover:text-white transition-colors cursor-pointer">Contato</a></li>
            <li>
              <a href="https://ebiz.teamcollab.net" target="_blank" rel="noopener noreferrer"
                class="text-sm text-gray-500 hover:text-white transition-colors inline-flex items-center gap-1.5">
                Produtos
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
            </li>
          </ul>
        </div>

        <!-- Col 2: Legal -->
        <div>
          <h4 class="text-xs text-gray-400 font-semibold uppercase tracking-[0.2em] mb-5">Legal</h4>
          <ul class="space-y-3">
            <li><router-link to="/terms-of-use" class="text-sm text-gray-500 hover:text-white transition-colors">Termos
                de Uso</router-link></li>
            <li><router-link to="/terms-of-privacy"
                class="text-sm text-gray-500 hover:text-white transition-colors">Política de Privacidade</router-link>
            </li>
          </ul>
        </div>

        <!-- Col 3: Contato -->
        <div>
          <h4 class="text-xs text-gray-400 font-semibold uppercase tracking-[0.2em] mb-5">Contato</h4>
          <ul class="space-y-4">
            <li class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-neon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span class="text-sm text-gray-500">+55 (31) 99582-1453</span>
            </li>
            <li class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-neon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span class="text-sm text-gray-500">contato@teamcollab.net</span>
            </li>
          </ul>
        </div>

        <!-- Col 4: Redes Sociais -->
        <div>
          <h4 class="text-xs text-gray-400 font-semibold uppercase tracking-[0.2em] mb-5">Redes Sociais</h4>
          <div class="flex flex-col gap-3">
            <a href="https://www.linkedin.com/company/teamcollabspace/" target="_blank" rel="noopener noreferrer"
              class="flex items-center gap-3 text-sm text-gray-500 hover:text-white transition-colors group">
              <div
                class="w-9 h-9 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center group-hover:bg-white/10 group-hover:border-white/10 transition-all flex-shrink-0">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </div>
              LinkedIn
            </a>
            <a href="https://www.instagram.com/teamcollabnet" target="_blank" rel="noopener noreferrer"
              class="flex items-center gap-3 text-sm text-gray-500 hover:text-white transition-colors group">
              <div
                class="w-9 h-9 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center group-hover:bg-white/10 group-hover:border-white/10 transition-all flex-shrink-0">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </div>
              Instagram
            </a>
            <a href="https://www.facebook.com/people/TeamCollab/61578821924281/" target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 text-sm text-gray-500 hover:text-white transition-colors group">
              <div
                class="w-9 h-9 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center group-hover:bg-white/10 group-hover:border-white/10 transition-all flex-shrink-0">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </div>
              Facebook
            </a>
          </div>
        </div>
      </div>

      <!-- Bottom divider -->
      <div class="hr-glow mb-6"></div>

      <!-- Bottom bar -->
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <p class="text-xs text-gray-600">
          &copy; {{ new Date().getFullYear() }} TeamCollab. Todos os direitos reservados.
        </p>
        <div class="flex items-center gap-6 text-xs">
          <router-link to="/terms-of-use" class="text-gray-600 hover:text-white transition-colors">Termos de
            Uso</router-link>
          <router-link to="/terms-of-privacy" class="text-gray-600 hover:text-white transition-colors">Política de
            Privacidade</router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

function goToSection(id) {
  if (route.path !== '/') {
    router.push('/' + id)
    return
  }
  const el = document.querySelector(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>
